import {
  adminMleVars,
  defaultMleVars,
  deleteMotorLeadExtensions,
  ecopetrolMleVars,
  kocMleVars,
  restoreMotorLeadExtensions
} from "@/models/equipment/mle/predicates";
import type { Motor_Lead_Extensions } from "gql/api";
import {
  useDeleteMlesMutation,
  useInsertMleMutation,
  useMotorLeadExtensionsQuery,
  useRestoreMlesMutation,
  useUpdateMleByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { mleTableColumns } from "./mleTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useMles(isAdmin: boolean): UseEquipment<Motor_Lead_Extensions> {
  const baseEquipment = useEquipment<Motor_Lead_Extensions>(isAdmin);
  const title = "MLE";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocMleVars;
    if (user.is_ecopetrol_customer) return ecopetrolMleVars;
    if (user.is_admin) return adminMleVars;
    return defaultMleVars;
  };
  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useMotorLeadExtensionsQuery({
    variables: getVariables()
  });

  const allRows = computed(
    () =>
      data.value?.motor_lead_extensions as Motor_Lead_Extensions[] | undefined
  );

  const hashOrder: (keyof Motor_Lead_Extensions)[] = [
    "manufacturer",
    "part_number",
    "family"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertMles } = useInsertMleMutation();
  const { executeMutation: executeUpdateMles } = useUpdateMleByPkMutation();
  const { executeMutation: executeDeleteMles } = useDeleteMlesMutation();
  const { executeMutation: executeRestoreMles } = useRestoreMlesMutation();

  const insertMles = (
    motor_lead_extensions: Omit<Motor_Lead_Extensions, "sizing_import" | "user">
  ) => executeInsertMles({ object: motor_lead_extensions });

  const updateMles = (id: string, mle: Motor_Lead_Extensions) =>
    executeUpdateMles({ id, object: mle });

  const deleteMles = (ids: string[]) =>
    executeDeleteMles({
      where: deleteMotorLeadExtensions(ids)
    });

  const restoreMles = (ids: string[]) =>
    executeRestoreMles({
      where: restoreMotorLeadExtensions(ids)
    });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "motor_lead_extensions",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    family: "",
    part_number: "",
    series: null,
    gauge: null,
    kv: null,
    amp_factor: null,
    resistance_per_1000_ft: null,
    impedance_60_hz_per_1000_ft: null,
    width_inches: null,
    depth_inches: null,
    length_feet: null,
    pound_per_foot: null
  }));

  return {
    searchPlaceholder: "Search Mles...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "375"`,
    columns: defineVisibleColumns(mleTableColumns),
    handleUpsert: defineUpsert(insertMles, updateMles),
    handleDelete: defineDelete(deleteMles),
    handleRestore: defineRestore(restoreMles),
    totalCount: computed(
      () => data.value?.motor_lead_extensions_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: mleTableColumns,
      globalSearchFields: ["series", "part_number", "length_feet"],
      allRows,
      title
    })
  };
}
